/**
 * CSV Export Utility
 * Converts health trend data to CSV format and triggers download
 */

import type { MaturityTrendDataPoint } from '../../components/Accuracy/MaturityTrendChart';

/**
 * Convert health trend data to CSV string
 */
export function convertTrendDataToCSV(data: MaturityTrendDataPoint[]): string {
  if (!data || data.length === 0) {
    return '';
  }

  // CSV header
  const headers = [
    'Date',
    'Overall Status',
    'Total Mature Signals',
    'Total Pending Signals',
    'Total Captured Signals',
    'Healthy Signals',
    'Degraded Signals',
    'Unhealthy Signals',
    'Unknown Signals'
  ];

  // CSV rows
  const rows = data.map(snapshot => [
    snapshot.snapshot_date,
    snapshot.overall_status,
    snapshot.total_mature_signals.toString(),
    snapshot.total_pending_signals.toString(),
    snapshot.total_captured_signals.toString(),
    snapshot.healthy_signals.toString(),
    snapshot.degraded_signals.toString(),
    snapshot.unhealthy_signals.toString(),
    snapshot.unknown_signals.toString()
  ]);

  // Combine header and rows
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  return csvContent;
}

/**
 * Download CSV file to user's computer
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Create a Blob from the CSV string
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  // Append to body, click, and remove
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  URL.revokeObjectURL(url);
}

/**
 * Export health trend data as CSV file
 */
export function exportTrendDataAsCSV(data: MaturityTrendDataPoint[]): void {
  const csvContent = convertTrendDataToCSV(data);

  if (!csvContent) {
    console.warn('No data to export');
    return;
  }

  // Generate filename with current date
  const today = new Date().toISOString().split('T')[0];
  const filename = `signal-maturity-trend-${today}.csv`;

  downloadCSV(csvContent, filename);
}
